import { Box, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  applyEvent,
  Prefectures,
  Status,
  SubsidyApplyKbn,
} from "../../assets/data/Constants";
import TkTable from "../../components/atoms/TkTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import { GlobalContext } from "../../context/globalContext";
import { useConcatRoutes } from "../../routes/router";
import { IAPI_SUB002_Response, IAPI_SUB003_Request } from "../../type/apiType";
import { TkGetCode } from "../../utils/TkGetCode";
import { changeAmountFormat } from "../../utils/useTools";
import "./_style.scss";
import { httpAPI_APP016, httpAPI_SUB002, httpAPI_SUB003 } from "./api/request";
import { headList, schema, tableList } from "./parts";
import TkDialog from "../../components/atoms/TkDialog";
import _ from "lodash";
import { TkRouteContext } from "../../context/tkRouteContext";
import TkKikoStepper from "../../components/molecules/TkKikoStepper";
import TkViewButtonJudgement from "../../components/molecules/TkViewButtonJudgement";
import TkMemo from "../../components/molecules/TkMemo";

/**
 * SCRW210002 補助金交付申請（概算交付申請内容）
 * 機構職員が国・各都道府県の概算交付申請内容を確認する。また、国・各都道府県の回答を登録する。
 * @return SCRW210002
 */
const SCRW210002 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeList = useConcatRoutes();
  const route = routeList.find((route) => route.path === location.pathname);
  const { state, setState } = useContext(GlobalContext);
  const [stateTableList, setStateTableList] = useState(_.cloneDeep(tableList));
  const [res, setRes] = useState<IAPI_SUB002_Response>({});
  const [tel, setTel] = useState("");
  const [fax, setFax] = useState("");
  const [open, setOpen] = useState(false);
  const [req, setReq] = useState<IAPI_SUB003_Request>({});
  const [memo, setMemo] = useState("");
  const { state: stateRouter, setState: setStateRouter } =
    useContext(TkRouteContext);
  const routerList = stateRouter.routerList || [];
  const isFromHome = routerList[routerList.length - 2]?.href === "/SCRW110001";
  const [isProcecing, setIsProcecing] = useState(false);

  const handleCallApiInit = async () => {
    // 補助金交付申請参照API呼び出し
    try {
      const res = await httpAPI_SUB002({
        applyId: location.state?.applyId,
        year: location.state?.year,
        prefectureCd: location.state?.prefectureCd,
        subsidyApplyKbn: location.state?.subsidyApplyKbn,
      });

      // 初期表示
      if (res.resultCd === "I0000") {
        let resArr = stateTableList;

        resArr.find(
          (item) => item.keyword === "year"
        )!.text = `${res.year}年度`;

        resArr.find((item) => item.keyword === "prefectureCd")!.text =
          TkGetCode(Prefectures, res.prefectureCd);

        const isEstimatePayApply =
          res.subsidyApplyKbn === SubsidyApplyKbn["概算交付申請"];

        if (isEstimatePayApply) {
          resArr.find(
            (item) => item.keyword === "unitAmt"
          )!.text = `${changeAmountFormat(res.estimateUnitAmt!)}円`;
        } else {
          resArr.find(
            (item) => item.keyword === "unitAmt"
          )!.text = `${changeAmountFormat(res.addUnitAmt!)}円`;
        }

        resArr.find(
          (item) => item.keyword === "staffNum"
        )!.text = `${changeAmountFormat(res.estimateStaffNum!)}人`;

        let payApplyAmt = resArr.find(
          (item) => item.keyword === "payApplyAmt"
        )!;
        payApplyAmt.title = isEstimatePayApply ? "金額" : "追加交付申請金額";
        payApplyAmt.text = `${changeAmountFormat(
          isEstimatePayApply ? res.estimatePayApplyAmt! : res.addApplyPayAmt!
        )}円`;

        for (let i in res) {
          let targetObj = resArr.find((item) => item.keyword === i)!;

          targetObj && (targetObj.setDefaultValue = res[i as keyof typeof res]);
        }

        resArr.find((item) => item.keyword === "planDate")!.setDefaultValue =
          isEstimatePayApply ? res.estimatePlanDate : res.addDecisionPlanDate;

        resArr.find((item) => item.keyword === "planAmt")!.setDefaultValue =
          isEstimatePayApply ? res.estimatePlanAmt : res.addDecisionPlanAmt;

        let list = isEstimatePayApply
          ? res.estimatePayApplyList
          : res.addPayApplyList;

        const bodyList: any = [
          [
            { children: TkGetCode(Prefectures, res.prefectureCd) },
            {
              children: (
                <Box className="box">
                  施設
                  <span />
                  職員
                </Box>
              ),
            },
            ...(list?.[0].prefectureList?.[0]
              ? Object.entries(list[0].prefectureList?.[0]).map(
                  (item: any) => ({
                    children: (
                      <Box className="box">
                        {changeAmountFormat(item[1][0].facilityNum)}
                        <span />
                        {changeAmountFormat(item[1][0].staffNum)}
                      </Box>
                    ),
                    align: "right",
                  })
                )
              : []),
          ],
          [
            { children: "全国" },
            {
              children: (
                <Box className="box">
                  施設
                  <span />
                  職員
                </Box>
              ),
            },
            ...(list?.[0].prefectureList?.[1]
              ? Object.entries(list[0].prefectureList?.[1]).map(
                  (item: any) => ({
                    children: (
                      <Box className="box">
                        {changeAmountFormat(item[1][0].facilityNum)}
                        <span />
                        {changeAmountFormat(item[1][0].staffNum)}
                      </Box>
                    ),
                    align: "right",
                  })
                )
              : []),
          ],
        ];

        resArr.find((item) => item.keyword === "payApply")!.node = (
          <Box width="100%">
            <TkTable
              headList={headList}
              bodyList={bodyList}
              headerLeftComponent={
                <Typography>
                  {TkGetCode(SubsidyApplyKbn, res.subsidyApplyKbn)}
                  内訳
                </Typography>
              }
            />
          </Box>
        );

        const bodyListYear: any = [
          [
            { children: TkGetCode(Prefectures, res.prefectureCd) },
            {
              children: (
                <Box className="box">
                  施設
                  <span />
                  職員
                </Box>
              ),
            },
            ...(res.yearAllPayApplyList?.[0].prefectureList?.[0]
              ? Object.entries(
                  res.yearAllPayApplyList?.[0].prefectureList?.[0]
                ).map((item: any) => ({
                  children: (
                    <Box className="box">
                      {changeAmountFormat(item[1][0].facilityNum)}
                      <span />
                      {changeAmountFormat(item[1][0].staffNum)}
                    </Box>
                  ),
                  align: "right",
                }))
              : []),
          ],
          [
            { children: "全国" },
            {
              children: (
                <Box className="box">
                  施設
                  <span />
                  職員
                </Box>
              ),
            },
            ...(res.yearAllPayApplyList?.[0].prefectureList?.[1]
              ? Object.entries(
                  res.yearAllPayApplyList?.[0].prefectureList?.[1]
                ).map((item: any) => ({
                  children: (
                    <Box className="box">
                      {changeAmountFormat(item[1][0].facilityNum)}
                      <span />
                      {changeAmountFormat(item[1][0].staffNum)}
                    </Box>
                  ),
                  align: "right",
                }))
              : []),
          ],
        ];

        let yearAllPayApply = resArr.find(
          (item) => item.keyword === "yearAllPayApply"
        )!;
        yearAllPayApply.showLine = !isEstimatePayApply;
        yearAllPayApply.node = (
          <Box width="100%">
            <TkTable
              headList={headList}
              bodyList={bodyListYear}
              headerLeftComponent={
                <Typography>
                  令和{res.year! - 2018}年度交付申請全体の内訳
                </Typography>
              }
            />
          </Box>
        );

        const statusList = [null, Status["届出待ち"], Status["差戻し一時保存"]];
        const isProcecing = !statusList.includes(res.latestApplyStatus as any);
        resArr.forEach((item) => {
          item.disabled = isProcecing;
        });
        setIsProcecing(isProcecing);

        // 処理履歴
        let memo = resArr.find((item) => item.keyword === "memo")!;
        let commentList: any = res.statusList?.map((item) => [
          {
            children: item.managerName,
          },
          {
            children: item.process,
          },
          {
            children: item.createDate,
            align: "center",
          },
          {
            children: item.processComment,
          },
        ]);
        memo.showLine = isFromHome && isProcecing;
        memo.node = (
          <TkMemo
            bodyList={commentList}
            disabledCheckBox
            onChange={handleChangeMemo}
          />
        );

        setStateTableList([...resArr]);

        setRes(res);

        const title = `補助金交付申請（${
          TkGetCode(SubsidyApplyKbn, res.subsidyApplyKbn) ?? ""
        }内容）`;
        route?.meta && (route.meta.title = title);
        let resRouter = stateRouter;
        let resRouterList = resRouter.routerList || [];
        if (resRouterList) {
          resRouterList[resRouterList.length - 1].title = title;
          setStateRouter(resRouter);
        }

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  useEffect(() => {
    if (routerList[routerList.length - 1]?.href !== "/SCRW210002") return;

    handleCallApiInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerList]);

  useEffect(() => {
    let resArr = stateTableList;
    let contactTel = resArr.find((item) => item.keyword === "contactTel")!;
    let contactFax = resArr.find((item) => item.keyword === "contactFax")!;

    contactTel.onGetInputValue = (value: any) => {
      setTel(value.target.valueForShow);
    };
    contactFax.onGetInputValue = (value: any) => {
      setFax(value.target.valueForShow);
    };

    setStateTableList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const handleChangeMemo = (e: any) => {
    setMemo(e.target.value);
  };

  const makeReq = (data: any): IAPI_SUB003_Request => {
    const isEstimatePayApply =
      res.subsidyApplyKbn === SubsidyApplyKbn["概算交付申請"];

    let reqObj: IAPI_SUB003_Request = {
      applyId: location.state?.applyId,
      year: res.year,
      prefectureCd: res.prefectureCd,
      managerDeployment: data.managerDeployment,
      managerName: data.managerName,
      contactTel: tel,
      contactFax: fax,
      [isEstimatePayApply ? "estimatePlanDate" : "addDecisionPlanDate"]:
        data.planDate,
      [isEstimatePayApply ? "estimatePlanAmt" : "addDecisionPlanAmt"]:
        data.planAmt,
      dueDate: data.dueDate,
      paymentPlanAmt: data.paymentPlanAmt,
      paymentTimes: Number(data.paymentTimes),
      subsidyApplyKbn: res.subsidyApplyKbn,
      screenId: "SCRW210002",
    };

    return reqObj;
  };

  const handleSubmit = async (data: any) => {
    setReq(makeReq(data));
    setOpen(true);
  };

  const onSave = async () => {
    // 補助金交付申請更新API呼び出し
    try {
      const res = await httpAPI_SUB003({
        ...req,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });

        navigate("/SCRW210001");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
        setOpen(false);
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const handleApplyStatusUpdate = async (btnName: keyof typeof applyEvent) => {
    let event = applyEvent[btnName];
    const statusList = [
      applyEvent["担当者に差戻し"],
      applyEvent["受理"],
      applyEvent["課長代理確認"],
      applyEvent["上長承認"],
    ];
    if (!statusList.includes(event)) return;

    if (
      event === applyEvent["担当者に差戻し"] &&
      res.latestApplyStatus === Status["受理待ち"]
    ) {
      event = applyEvent["契約者に差戻し"];
    }

    try {
      // 申請状況更新API 呼び出し
      const res = await httpAPI_APP016({
        applyId: location.state?.applyId,
        applyEvent: event,
        screenId: "SCRW210002",
        reason: memo,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });

        navigate("/SCRW110001");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  return (
    <Box data-testid="SCRW210002" className="SCRW210002">
      {isFromHome && (
        <TkKikoStepper
          applicationFlowType={"TYPE3"}
          businessType={"JOB-82"}
          applicationStatus={res.latestApplyStatus as Status}
        />
      )}

      <TkFakeTable
        list={stateTableList}
        ruleSchema={schema}
        buttonGroup={
          <>
            <TkBackButton mr={isProcecing} />
            {isFromHome && (
              <TkViewButtonJudgement
                flowType={"TYPE3"}
                Status={res.latestApplyStatus as Status}
                businessType={"JOB-82"}
                onClick={handleApplyStatusUpdate}
              />
            )}
          </>
        }
        submitButtonText="内容登録"
        showButton={!isProcecing && res.resultCd === "I0000"}
        onCheckAndSubmit={handleSubmit}
      />

      <TkDialog
        title="確認（回答を登録します）"
        contextText="　登録を行いますが、問題ないでしょうか。"
        open={open}
        okText="はい"
        cancelText="いいえ"
        onCancel={() => setOpen(false)}
        onSave={() => onSave()}
      />
    </Box>
  );
};

export default SCRW210002;
